<template>
    <section
        :id="block.id"
        :data-theme="colorTheme"
        :data-mode="mode"
        class="relative w-full mx-auto surface-primary"
        :class="[
            spacing,
            {
                [padding]: componentName !== 'DividerBlock',
                [radius]: !blockSettings?.FullWidthBackground,
                [size]: !blockSettings?.FullWidthBackground,
                'overflow-hidden': blockSettings.FullWidthBackground || blockSettings.ContainerWidth === 'full-width',
                'last-of-type:mb-20': blockSettings.ContainerWidth !== 'full-width' && !blockSettings?.FullWidthBackground && !isNewsletterArticle,
                'full-width-bg': blockSettings.FullWidthBackground && isEmpty(blockSettings?.BackgroundElement) // Used to remove top padding when full-width background is set
            }

        ]"
    >
        <component
            :is="blockComponent"
            v-if="block.canonicalUid"
            :id="block.id"
            :block="blockSettings"
            class="mx-auto"
            :class="[
                size,
            ]"
        />
        <BackgroundElement
            v-if="blockSettings?.BackgroundElement?.length > 0"
            :entry="blockSettings?.BackgroundElement[0]"
            class="z-10"
        />
    </section>
</template>

<script setup>
    import BackgroundElement from '~/components/blocks/BackgroundElement.vue'

    const props = defineProps({
        block: Object,
        parentHasBgElement: Number,
        isSingleton: Boolean,
        isNewsletterArticle: Boolean,
    })

    const componentName = computed(() => {
        let name = props.block.__typename

        // remove _ds and _Entry from the name
        name = replace(name, 'ds_', '')
        name = replace(name, '_Entry', '')

        return name
    })

    const blockSettings = computed(() => {
        return mapKeys(props.block, (value, key) => {
            // remove prefix from key e.g. table_BgTitleOnly => BgTitleOnly
            return replace(key, /.*_/, '')
        })
    })

    const blockComponent = defineAsyncComponent({
        loader: () => import(`~/components/blocks/${upperFirst(componentName.value)}/block.async.vue`)
    })

    const colorTheme = computed(() => {
        return blockSettings.value.ColorTheme
    })

    const mode = computed(() => {
        switch (blockSettings.value.ColorPalette) {
            case 'color-subtle':
                return 'brand'
            case 'color-invert':
                return 'brand-invert'
            default:
                return blockSettings.value.ColorPalette
        }
    })

    const spacing = computed(() => {
        if (props.isNewsletterArticle) {
            return ''
        }

        switch (blockSettings.value.TopSpacing) {
            case 'lg':
                return 'top-spacing-lg'
            case 'md':
            default:
                return 'top-spacing-md'
            case 'sm':
                return 'top-spacing-sm'
            case 'none':
                return 'top-spacing-none'
        }
    })

    const size = computed(() => {
        // newsletter articles are always full width
        if (props.isNewsletterArticle) {
            return ''
        }

        switch (blockSettings.value?.ContainerWidth) {
            case 'full-width':
                return ''
            case 'wide':
                return 'container-wide'
            case 'extra-wide':
                return 'container-extra-wide'
            default:
                return 'container'
        }
    })

    const padding = computed(() => {

        if (blockSettings.value.FullWidthBackground) {
            return 'px-4 lg:px-8 py-6 lg:py-24'
        }

        if (blockSettings.value.ContainerWidth === 'full-width') {
            return ''
        }

        switch (mode.value) {
            // These palettes need full padding; handles bug where no palette is selected
            case 'brand-invert':
            case 'brand':
                return 'px-4 py-8 lg:px-16 lg:py-16'
            default:
                return 'px-4 lg:px-0'
        }
    })

    const radius = computed(() => {
        switch (blockSettings.value.ContainerWidth) {
            case 'full-width':
                return ''
            default:
                return 'lg:rounded-card-lg'
        }
    })



</script>
